import React, { useEffect, useMemo, useState } from 'react';
import BranchFormModal from './component/BranchFormModal'; // ← fixed path
import {
  fetchBranches,
  createBranch,
  updateBranch,
  deleteBranch,
} from '@/services/BranchService';
import ToastMe from '@/components/ui/ToastMe';

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
    {children}
  </span>
);

const ActionButton = ({ kind = 'primary', children, ...rest }) => {
  const styles = {
    primary:
      'inline-flex items-center gap-1 rounded border border-blue-500 px-3 py-1 text-blue-600 hover:bg-blue-50 text-xs',
    green:
      'inline-flex items-center gap-1 rounded border border-green-500 px-3 py-1 text-green-600 hover:bg-green-50 text-xs',
    red:
      'inline-flex items-center gap-1 rounded border border-red-500 px-3 py-1 text-red-600 hover:bg-red-50 text-xs',
  }[kind];
  return (
    <button type="button" className={styles} {...rest}>
      {children}
    </button>
  );
};

export default function BranchesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create'); // 'create' | 'edit' | 'view'

  const title = 'Branches';

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchBranches();
      setRows(data || []);
    } catch (e) {
      ToastMe(e?.message || 'Failed to load branches', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setMode('create');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setMode('edit');
    setModalOpen(true);
  };

  const openView = (row) => {
    setEditing(row);
    setMode('view');
    setModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete branch “${row?.name}”?`)) return;
    try {
      await deleteBranch(row._id);
      ToastMe('Branch deleted', 'success');
      load();
    } catch (e) {
      ToastMe(e?.message || 'Delete failed', 'error');
    }
  };

  const handleSubmit = async (payload) => {
    try {
      if (mode === 'edit' && editing?._id) {
        await updateBranch(editing._id, payload);
        ToastMe('Branch updated', 'success');
      } else {
        await createBranch(payload);
        ToastMe('Branch created', 'success');
      }
      setModalOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      ToastMe(e?.message || 'Save failed', 'error');
    }
  };

  const tableBody = useMemo(() => {
    if (loading) {
      return (
        <tr>
          <td colSpan={5} className="p-6 text-center text-sm text-gray-500">
            Loading…
          </td>
        </tr>
      );
    }
    if (!rows.length) {
      return (
        <tr>
          <td colSpan={5} className="p-6 text-center text-sm text-gray-500">
            No branches found
          </td>
        </tr>
      );
    }
    return rows.map((r) => (
      <tr key={r._id} className="border-b last:border-0">
        <td className="px-4 py-3 text-sm text-gray-700">{r.name}</td>
        <td className="px-4 py-3 text-sm">
          {Number(r.status) === 1 ? <Badge>Active</Badge> : <span className="text-gray-500">Inactive</span>}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">{r.city || '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-500">{r.state || '-'}</td>
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <ActionButton kind="primary" onClick={() => openView(r)}>
              <i className="fa-regular fa-eye" /> View
            </ActionButton>
            <ActionButton kind="green" onClick={() => openEdit(r)}>
              <i className="fa-regular fa-pen-to-square" /> Edit
            </ActionButton>
            <ActionButton kind="red" onClick={() => handleDelete(r)}>
              <i className="fa-regular fa-trash-can" /> Delete
            </ActionButton>
          </div>
        </td>
      </tr>
    ));
  }, [rows, loading]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white shadow hover:opacity-95"
          >
            <i className="fa-solid fa-plus" /> Add Branch
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-t border-gray-100">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">City</th>
                <th className="px-4 py-3 font-medium">State</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>{tableBody}</tbody>
          </table>
        </div>

        <div className="px-6 py-4 text-sm text-gray-500">
          Showing {rows.length ? `1 to ${rows.length}` : '0'} of {rows.length} entries
        </div>
      </div>

      <BranchFormModal
        open={modalOpen}
        initial={editing}
        mode={mode}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
